import { CopyWellDetail, ProteinLayerDetailsDTO } from 'business/models/CustomModels';
import {
  ConditionDTO,
  ConditionIngredientDTO,
  CustomLayerDTO,
  FragmentFragmentScreen,
  FragmentLayer,
  FragmentLayerDTO,
  FragmentScreen,
  GridDTO,
  GridIngredientDTO,
  GridLayerDTO,
  IngredientDTO,
  IngredientStock,
  LayerDetailsDTO,
  LayerDTO,
  LayerStateDTO,
  LayerTypeDTO,
  ProteinFormulationDTO,
  ProteinLayerDTO,
  ProteinLayerProteinFormulationDTO,
  RandomLayerDTO,
  RandomLayerExcludeListDTO,
  RandomLayerGenerationListDTO,
  RandomLayerGroupIngredientDTO,
  RandomLayerIngredientGroupDTO,
  ScreenLayerDTO,
  ScreenLotDTO,
  SeedLayerDetailsDTO,
  TreeNode,
  WellLayerDTO,
  WellLayerIngredientDTO,
} from 'business/models/Models';
import {
  getLinkedLayerGridDto,
  getUniqueLayerName,
} from 'business/plateviewer/GenerateUtil';
import {
  FindOptimumMatrixSize,
  getIngredientByIngIngTypeId,
  GetIngredients,
  getIngredientStock,
  getNewId,
  isNullOrUndefined,
  showSnackBar,
} from 'utils/Global';
import { EGridVaryAlong } from 'enums/ELayersView';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { useRMDispatch } from 'redux/hooks';
import { pasteNewLayer } from 'redux/reducers/experimentDataReducer';
import { WellLayer, WellLayerIngredientDto } from 'components/Experiment/constants';

export const pasteAsNewLayer = () => {
  const dispatch = useRMDispatch();
  return (
    layerType,
    docData: ExperimentDtoClient,
    selectedWells: CopyWellDetail[],
    wellNumber?,
  ) => {
    selectedWells = selectedWells.filter(
      (w) =>
        !isNullOrUndefined(
          w.wellLayers.find((wl) => wl.wellLayerIngredientList.length > 0),
        ),
    );
    const totalWellsAvailable =
      docData.containerCols * docData.colCount * docData.containerRows * docData.rowCount;
    if (totalWellsAvailable < selectedWells.length) {
      showSnackBar(
        'The number of wells exceeds the size of the canvas. Check the number of wells or increase the size of the canvas and try again.',
      );
      return false;
    }

    dispatch(
      pasteNewLayer({
        layerType: layerType,
        selectedWells: selectedWells,
        wellNumber: wellNumber,
      }),
    );
    return true;
  };
};

function getSelectedWellsRectSize(selectedWellRows) {
  if (selectedWellRows.length > 1) {
    //**
    /* this sort function is causing order probelm when we paste custom layer wells.
    /* If there is problem with custom layer wells order,
    /* then we need to uncomment this sort function and fix the order of pasted wells incase of custom layer.
    /*
     */
    // selectedWellRows.sort(function (a, b) {
    //   return a.x - b.x || a.y - b.y;
    // });
    for (let i = 0; i < selectedWellRows.length; i++) {
      const wellLocation = selectedWellRows[i];
      if (
        wellLocation.x < selectedWellRows[0].x ||
        wellLocation.x > selectedWellRows[selectedWellRows.length - 1].x ||
        wellLocation.y < selectedWellRows[0].y ||
        wellLocation.y > selectedWellRows[selectedWellRows.length - 1].y
      ) {
        return FindOptimumMatrixSize(selectedWellRows.length);
      }
    }
  }
  const width = selectedWellRows[selectedWellRows.length - 1].x - selectedWellRows[0].x;
  const height = selectedWellRows[selectedWellRows.length - 1].y - selectedWellRows[0].y;

  //If it is not a complete rectangle i.e. not all wells in rectangle have been selected.
  if ((width + 1) * (height + 1) != selectedWellRows.length) {
    return FindOptimumMatrixSize(selectedWellRows.length);
  }

  return { width, height };
}

function GetValidCanvasRectangle(x, y, width, height, containerCol, containerRow) {
  // point and width make a well participate twice in the calculation
  // so subtracting 1 out of the equation.
  if (x + width + 1 >= containerCol) {
    x -= x + width + 1 - containerCol;
  }

  if (y + height + 1 > containerRow) {
    y -= y + height + 1 - containerRow;
  }

  // x, y can go -ve when canvas width/height is less than width/height
  if (x < 0) {
    x = 0;
  }

  if (y < 0) {
    y = 0;
  }

  return { x, y, width, height };
}
export function initLayer(
  index: number,
  type: LayerTypeDTO,
  docData: ExperimentDtoClient,
  wellCount,
  wellNumber?: number,
) {
  const stackCount = Math.ceil(wellCount / (docData.colCount * docData.containerCols));
  const width = Math.floor(
    (docData.colCount * docData.containerCols * stackCount) / wellCount,
  );
  let layerName = '';
  const newLayer = new LayerDTO();
  newLayer.id = getNewId();
  if (type === LayerTypeDTO.Random) {
    layerName = 'Random Layer';
    newLayer.x = 0; // width % index;
    newLayer.y = 0; //width / index;
    newLayer.height = docData.containerRows - 1; //0;
    newLayer.width = docData.containerCols - 1; // selectedWells.length - 1;
  } else if (type === LayerTypeDTO.Screen) {
    layerName = 'Screen Layer';
    newLayer.x = 0; // width % index;
    newLayer.y = 0; //width / index;
    newLayer.height = docData.containerRows - 1; //0;
    newLayer.width = docData.containerCols - 1; // selectedWells.length - 1;
  } else if (type === LayerTypeDTO.ConditionList) {
    layerName = 'Custom Layer';
    newLayer.y = parseInt(((wellNumber - 1) / docData.containerCols).toString()); // width % index;
    newLayer.x = (wellNumber - 1) % docData.containerCols; //width / index;
    const matrix = getSelectedWellsRectSize(wellCount);
    const validMatrix = GetValidCanvasRectangle(
      newLayer.x,
      newLayer.y,
      matrix.width,
      matrix.height,
      docData.containerCols,
      docData.containerRows,
    );
    newLayer.x = validMatrix.x;
    newLayer.y = validMatrix.y;
    newLayer.width = validMatrix.width; //0;
    newLayer.height = validMatrix.height; // selectedWells.length - 1;
  } else if (type === LayerTypeDTO.Protein) {
    layerName = 'Protein Layer';
    newLayer.x = 0; // width % index;
    newLayer.y = 0; //width / index;
    newLayer.height = docData.containerRows - 1; //0;
    newLayer.width = docData.containerCols - 1; // selectedWells.length - 1;
  } else if (type === LayerTypeDTO.Seed) {
    layerName = 'Seed Layer';
    newLayer.x = 0;
    newLayer.y = 0;
    newLayer.height = docData.containerRows - 1;
    newLayer.width = docData.containerCols - 1;
  } else {
    layerName = 'Grid Layer';
    newLayer.x = (index * width) % (docData.colCount * docData.containerCols);
    newLayer.y = 0;
    if (index < wellCount - 1) {
      newLayer.width = width - 1;
    } else {
      newLayer.width = docData.colCount * docData.containerCols - newLayer.x - 1;
    }
    newLayer.height = Math.floor(
      (docData.rowCount * docData.containerRows) / stackCount - 1,
    );
    const stackRow = Math.ceil(
      (index * width + width) / (docData.colCount * docData.containerCols),
    );
    if (stackRow > 1 && stackCount > 0) {
      newLayer.y += (newLayer.height + 1) * (stackRow - 1);
      if (stackRow === stackCount) {
        newLayer.height = docData.rowCount * docData.containerRows - newLayer.y - 1;
      }
    }
  }
  newLayer.volume = getDefaultVolume(docData);
  newLayer.targetDrop = -1;
  newLayer.overrideLayer = false;
  newLayer.locked = false;
  newLayer.expId = docData.experimentId;
  newLayer.layerState = LayerStateDTO.New;
  newLayer.volumeBasedAddition = false;
  newLayer.dirty = true;
  newLayer.expId = docData.experimentId;

  newLayer.type = type;
  const layerDetail = initLayerDetail(type, newLayer.id, docData);

  newLayer.name = getUniqueLayerName(docData.layers, layerName);
  return { newLayer: newLayer, layerDetail: layerDetail };
}

export function initLayerDetail(type: LayerTypeDTO, layerId, docData) {
  let layerDetail;

  switch (type) {
    case LayerTypeDTO.Grid:
      layerDetail = new GridLayerDTO();
      layerDetail.grid = new GridDTO();
      layerDetail.grid.id = getNewId();
      layerDetail.id = getNewId();
      layerDetail.layerId = layerId;
      layerDetail.grid.experimentId = docData.experimentId;
      layerDetail.grid.stopWellVolume = getDefaultVolume(docData);
      layerDetail.gridId = layerDetail.grid.id;
      layerDetail.subGridNumber = 1;
      layerDetail.grid.gridIngredient = [];
      layerDetail.grid.startWellVolume = getDefaultVolume(docData);
      layerDetail.grid.wellVolumeVaryAlong = EGridVaryAlong.NONE;
      break;
    case LayerTypeDTO.ConditionList:
      layerDetail = new CustomLayerDTO();
      (layerDetail as CustomLayerDTO).experimentId = docData.experimentId;
      (layerDetail as CustomLayerDTO).wellVolume = getDefaultVolume(docData);
      (layerDetail as CustomLayerDTO).layerId = layerId;
      (layerDetail as CustomLayerDTO).id = getNewId();
      (layerDetail as CustomLayerDTO).condition = [];
      break;
    case LayerTypeDTO.Random:
      layerDetail = new RandomLayerDTO();
      (layerDetail as RandomLayerDTO).experimentId = docData.experimentId;
      (layerDetail as RandomLayerDTO).wellVolume = getDefaultVolume(docData);
      (layerDetail as RandomLayerDTO).layerId = layerId;
      (layerDetail as RandomLayerDTO).randomLayerIngredientGroup = [];
      (layerDetail as RandomLayerDTO).randomLayerExcludeList = [];
      (layerDetail as RandomLayerDTO).gridSteps = 10;
      (layerDetail as CustomLayerDTO).id = getNewId();
      break;
    case LayerTypeDTO.Screen:
      layerDetail = new ScreenLayerDTO();
      (layerDetail as ScreenLayerDTO).id = getNewId();
      (layerDetail as ScreenLayerDTO).layerId = layerId;
      break;
    case LayerTypeDTO.Protein:
      layerDetail = new ProteinLayerDTO();
      (layerDetail as ProteinLayerDetailsDTO).layerId = layerId;
      break;
    case LayerTypeDTO.Seed:
      layerDetail = new SeedLayerDetailsDTO();
      (layerDetail as SeedLayerDetailsDTO).layerId = layerId;
      (layerDetail as SeedLayerDetailsDTO).id = getNewId();
      (layerDetail as SeedLayerDetailsDTO).experimentId = docData.experimentId;
  }
  return layerDetail;
}

export function getDefaultVolume(docData) {
  const defaultWellVol = docData.defWellVolume;
  let vol = defaultWellVol;
  if (docData.layers.length === 0) {
    return defaultWellVol;
  } else {
    const concLayer = docData.layers.find((layer) => layer.volumeBasedAddition == false);
    if (isNullOrUndefined(concLayer)) {
      return defaultWellVol;
    } else {
      switch (concLayer.type) {
        case LayerTypeDTO.Grid:
          vol = getLinkedLayerGridDto(
            docData.layers,
            concLayer.layerDetail,
          ).startWellVolume;
          break;
        case LayerTypeDTO.Screen:
        case LayerTypeDTO.Protein:
          vol = concLayer.volume;
          break;
        case LayerTypeDTO.Random:
        case LayerTypeDTO.ConditionList:
          const customLayer = concLayer.layerDetail as CustomLayerDTO;
          vol = customLayer.wellVolume;
          break;
        default:
          break;
      }
      return vol;
    }
  }
}

export function addCustomLayerIngredients(conditionLyr: CustomLayerDTO, selectedWells) {
  selectedWells.forEach((well, i) => {
    well.wellLayers.forEach((wl: WellLayerDTO) => {
      const cond: ConditionDTO = new ConditionDTO();
      cond.id = getNewId();
      cond.conditionListLayerId = conditionLyr.id;
      cond.conditionIngredient = [];
      cond.conditionNumber = conditionLyr.condition.length + 1;
      wl.wellLayerIngredientList.forEach((wli: WellLayerIngredientDTO) => {
        const conditionIng = new ConditionIngredientDTO();
        conditionIng.id = getNewId();
        conditionIng.conditionId = conditionLyr.id;
        conditionIng.concentration = wli.concentration;
        conditionIng.highPhingredientStockId = wli.highPhIngStockId;
        conditionIng.ingredientIngredientTypeId = wli.ingredientIngredientTypeId;
        conditionIng.ingredientStockId = wli.ingredientStockId;
        conditionIng.pH = wli.pH;
        cond.conditionIngredient.push(conditionIng);
      });
      conditionLyr.condition.push(cond);
    });
  });
}
export function addRandomLayerGroupIngredients(
  ingGroups: RandomLayerIngredientGroupDTO[],
  layerId,
  selectedWells,
) {
  selectedWells.forEach((well, i) => {
    well.wellLayers.forEach((wl: WellLayerDTO) => {
      wl.wellLayerIngredientList.forEach((wli: WellLayerIngredientDTO) => {
        const ingGroup = ingGroups.find((ig) => ig.name === wli.ingredientTypeName);
        let groupIng = ingGroup.randomLayerGroupIngredient.find(
          (gi) => gi.ingredientStockId === wli.ingredientStockId,
        );
        if (!isNullOrUndefined(groupIng)) {
          if (groupIng.lowConcentration < wli.concentration) {
            groupIng.highConcentration = wli.concentration;
          } else {
            groupIng.lowConcentration = wli.concentration;
          }
        } else {
          groupIng = new RandomLayerGroupIngredientDTO();
          groupIng.id = getNewId();
          groupIng.highPh = wli.highPhIngStock?.stockPh;
          groupIng.highPhingredientStockId = wli.highPhIngStockId;
          groupIng.ingredientIngredientTypeId = wli.ingredientIngredientTypeId;
          groupIng.ingredientStockId = wli.ingredientStockId;
          groupIng.lowConcentration = wli.concentration;
          groupIng.highConcentration = wli.concentration;
          groupIng.lowPh = wli.pH;
          groupIng.randomLayerIngredientGroupId = ingGroup.id;
          groupIng.weight = 1;
          ingGroup.randomLayerGroupIngredient.push(groupIng);
        }

        if (ingGroup.randomLayerGenerationList.length == 0) {
          const genIng = new RandomLayerGenerationListDTO();
          genIng.id = getNewId();
          genIng.probability = 1;
          genIng.randomLayerId = layerId;
          genIng.randomLayerIngredientGroupId = ingGroup.id;
          ingGroup.randomLayerGenerationList.push(genIng);
        }
      });
    });
  });
}

export function SubstituteWithPreparedStock(selectedWells) {
  const unSubstitutedStockList: string[] = [];
  selectedWells.forEach((well, i) => {
    well.wellLayers.forEach((wl: WellLayerDTO) => {
      for (const wellLayerIng of wl.wellLayerIngredientList) {
        const conditionIng = wellLayerIng;
        const currentIngredientStock = getIngredientStock(conditionIng.ingredientStockId);
        if (currentIngredientStock.isPrepared) {
          if (isNullOrUndefined(conditionIng.highPhIngStockId)) {
            continue;
          }
          conditionIng.highPhIngStock = getIngredientStock(conditionIng.highPhIngStockId);
          if (conditionIng.highPhIngStock.isPrepared) {
            continue;
          }
        }
        const concGroupedIngredientStockRows = GetRelatedPreparedStocksGroupedbyConc(
          conditionIng.ingredientStock,
          conditionIng.ingredientTypeIsBuffer,
        );
        let foundMatch = false;

        if (
          !isNullOrUndefined(
            concGroupedIngredientStockRows[currentIngredientStock.stockConcentration],
          )
        ) {
          const groupIngredientStockList =
            concGroupedIngredientStockRows[currentIngredientStock.stockConcentration];
          const result: FindPreparedStockResult = FindFilterSubstituteStockList(
            conditionIng,
            currentIngredientStock,
            conditionIng.highPhIngStock,
            groupIngredientStockList,
            conditionIng.ingredientTypeIsBuffer,
          );
          if (result.found) {
            foundMatch = true;
            continue;
          }
          delete concGroupedIngredientStockRows[
            currentIngredientStock.stockConcentration
          ];
        }
        for (const conc in concGroupedIngredientStockRows) {
          let groupIngredientStockList;
          if (!isNullOrUndefined(concGroupedIngredientStockRows[conc])) {
            FindFilterSubstituteStockList(
              conditionIng,
              currentIngredientStock,
              conditionIng.highPhIngStock,
              groupIngredientStockList,
              conditionIng.ingredientTypeIsBuffer,
            );
            foundMatch = true;
            break;
          } else {
            showSnackBar(
              'Problem occured when getting value from Concentation Grouped StockRows for concentation:' +
                conc +
                '',
            );
          }
        }

        if (!foundMatch) {
          const ingStockIngredient = getIngredientByIngIngTypeId(
            conditionIng.ingredientIngredientTypeId,
          );
          const stockString: string =
            ingStockIngredient.longName + ' with StockID:' + currentIngredientStock.id;
          if (isNullOrUndefined(unSubstitutedStockList.find((s) => s === stockString))) {
            unSubstitutedStockList.push(stockString);
          }
        }
      }
    });
  });
  return unSubstitutedStockList;
}

function GetRelatedPreparedStocksGroupedbyConc(
  ingredientStock: IngredientStock,
  isBuffer: boolean,
) {
  const preparedStocks = GetIngredients().ingredientStock.filter(
    (ingStock) =>
      ingStock.ingredientId === ingredientStock.ingredientId &&
      ingStock.isBuffer === isBuffer &&
      ingStock.isPrepared === true &&
      ingStock.concentrationUnits === ingredientStock.concentrationUnits &&
      ingStock.stockConcentration >= ingredientStock.stockConcentration,
  );

  const concGroupedIngredientStockRows = {};
  const LIMIT = 10;
  let count = 0;
  for (let i = 0; i < preparedStocks.length; i++) {
    const prepStock = preparedStocks[i];
    if (isNullOrUndefined(concGroupedIngredientStockRows[prepStock.stockConcentration])) {
      if (count > LIMIT) {
        continue;
      }
      concGroupedIngredientStockRows[prepStock.stockConcentration] = [];
      count++;
    }
    concGroupedIngredientStockRows[prepStock.stockConcentration].push(prepStock);
  }
  return concGroupedIngredientStockRows;
}

function FindFilterSubstituteStockList(
  conditionIng: WellLayerIngredientDTO,
  currentIngStock: IngredientStock,
  currentIngHighPhStock: IngredientStock,
  groupIngredientStockList: IngredientStock[],
  isBuffer: boolean,
): FindPreparedStockResult {
  let ingredientStockList: IngredientStock[] = [];
  const filteredIngredientStockList: IngredientStock[] = [];
  if (
    !isNullOrUndefined(currentIngStock.vendorId) &&
    FilterListforVendorID(
      groupIngredientStockList,
      currentIngStock.vendorId,
      isBuffer,
      filteredIngredientStockList,
    )
  ) {
    ingredientStockList = filteredIngredientStockList;
  } else {
    ingredientStockList = groupIngredientStockList;
  }
  const findMatchingStockResult: FindPreparedStockResult =
    FindMatchingPreparedStockinList(
      conditionIng,
      currentIngStock,
      currentIngHighPhStock,
      ingredientStockList,
      isBuffer,
    );
  if (findMatchingStockResult.found) {
    SubstituteStockinConditionIngredientRow(
      conditionIng,
      findMatchingStockResult.matchingStockID,
      findMatchingStockResult.matchingHiPhStockID,
    );
    findMatchingStockResult.found = true;
    return findMatchingStockResult;
  }
  findMatchingStockResult.found = false;
  return findMatchingStockResult;
}

function FilterListforVendorID(
  ingredientStockList: IngredientStock[],
  vendorID: number,
  isBuffer: boolean,
  filterList: IngredientStock[],
) {
  const bufferList: number[] = [];

  for (const i in ingredientStockList) {
    const ingStock = ingredientStockList[i];
    if (isNullOrUndefined(ingStock.vendorId)) {
      continue;
    } else if (ingStock.vendorId === vendorID) {
      if (isBuffer) {
        if (
          isNullOrUndefined(bufferList.find((stockPh) => stockPh === ingStock.stockPh))
        ) {
          continue;
        } else {
          bufferList.push(ingStock.stockPh);
        }
      }
      filterList.push(ingStock);
    }
  }

  if (filterList.length > 0) {
    if (!isBuffer) {
      return true;
    }

    for (const i in ingredientStockList) {
      const stockRow = ingredientStockList[i];
      if (
        !isNullOrUndefined(bufferList.find((stockph) => stockph === stockRow.stockPh))
      ) {
        bufferList.push(stockRow.stockPh);
        filterList.push(stockRow);
      }
    }
    return true;
  }

  return false;
}

type FindPreparedStockResult = {
  matchingStockID: number;
  matchingHiPhStockID: number;
  found: boolean;
};

function FindMatchingPreparedStockinList(
  condIngredientRow: WellLayerIngredientDTO,
  currStockIngredientRow: IngredientStock,
  currStockHighPHIngredientRow: IngredientStock,
  ingredientStockRowList: IngredientStock[],
  isBuffer: boolean,
): FindPreparedStockResult {
  const result: FindPreparedStockResult = {
    matchingStockID: 0,
    matchingHiPhStockID: 0,
    found: false,
  };
  if (!ingredientStockRowList || ingredientStockRowList.length < 1) {
    return result;
  }
  let lowPhStockId = 0;
  let highPhStockId = 0;
  let lowPhValueDifference = Number.MAX_VALUE;
  let highPhValueDifference = Number.MAX_VALUE;

  for (const i in ingredientStockRowList) {
    const ingredientStockRow = ingredientStockRowList[i];
    if (!isBuffer) {
      result.matchingStockID = ingredientStockRow.id;
      result.found = true;
      return result;
    } else {
      if (condIngredientRow.pH == ingredientStockRow.stockPh) {
        result.matchingStockID = ingredientStockRow.id;
        result.matchingHiPhStockID = 0;
        result.found = true;
        return result;
      } else if (
        ingredientStockRow.stockPh <= currStockIngredientRow.stockPh &&
        ingredientStockRow.stockPh < condIngredientRow.pH
      ) {
        const difference = condIngredientRow.pH - ingredientStockRow.stockPh;
        if (difference < lowPhValueDifference) {
          lowPhValueDifference = difference;
          lowPhStockId = ingredientStockRow.id;
        }
      } else if (ingredientStockRow.stockPh > condIngredientRow.pH) {
        if (isNullOrUndefined(condIngredientRow.highPhIngStock)) {
          const difference: number = ingredientStockRow.stockPh - condIngredientRow.pH;
          if (difference < highPhValueDifference) {
            highPhValueDifference = difference;
            highPhStockId = ingredientStockRow.id;
          }
        } else if (ingredientStockRow.stockPh >= currStockHighPHIngredientRow.stockPh) {
          const difference: number =
            ingredientStockRow.stockPh - currStockHighPHIngredientRow.stockPh;
          if (difference < highPhValueDifference) {
            highPhValueDifference = difference;
            highPhStockId = ingredientStockRow.id;
          }
        }
      }
    }
  }

  if (highPhStockId != 0 && lowPhStockId != 0) {
    result.matchingStockID = lowPhStockId;
    result.matchingHiPhStockID = highPhStockId;
    result.found = true;
    return result;
  }
  result.found = false;
  return result;
}

function SubstituteStockinConditionIngredientRow(
  conditionIngredientRow: WellLayerIngredientDTO,
  preparedStockID: number,
  preparedHiPHStockID: number,
) {
  conditionIngredientRow.ingredientStockId = preparedStockID;
  conditionIngredientRow.ingredientStock = getIngredientStock(preparedStockID);
  if (preparedHiPHStockID != 0) {
    conditionIngredientRow.highPhIngStockId = preparedHiPHStockID;
    conditionIngredientRow.highPhIngStock = getIngredientStock(preparedHiPHStockID);
  } else {
    conditionIngredientRow.highPhIngStockId = null;
  }
}

export function copyLayer(
  sourceLayer: LayerDTO,
  expData?: ExperimentDtoClient,
  gridDto?: GridDTO,
): LayerDTO {
  const newLayer = new LayerDTO();
  newLayer.id = getNewId();
  const expId = expData.experimentId;

  newLayer.name = sourceLayer.name;
  newLayer.x = sourceLayer.x;
  newLayer.y = sourceLayer.y;
  newLayer.height = sourceLayer.height;
  newLayer.width = sourceLayer.width;

  newLayer.volume = sourceLayer.volume;
  newLayer.targetDrop = sourceLayer.targetDrop;
  newLayer.overrideLayer = sourceLayer.overrideLayer;
  newLayer.locked = sourceLayer.locked;
  newLayer.expId = expData.experimentId;
  newLayer.layerState = LayerStateDTO.New;
  newLayer.volumeBasedAddition = sourceLayer.volumeBasedAddition;
  newLayer.dirty = true;
  newLayer.type = sourceLayer.type;
  switch (sourceLayer.type) {
    case LayerTypeDTO.ConditionList:
      newLayer.layerDetail = copyCustomLayerDetail(
        sourceLayer,
        expId,
        newLayer.id,
        expData,
      );
      break;
    case LayerTypeDTO.Protein:
      newLayer.layerDetail = copyProteinLayerDetail(sourceLayer, expId, newLayer.id);
      break;
    case LayerTypeDTO.Screen:
      newLayer.layerDetail = copyScreenLayerDetail(sourceLayer, expId, newLayer.id);
      break;
    case LayerTypeDTO.Grid:
      newLayer.layerDetail = copyGridLayerDetail(
        sourceLayer,
        expId,
        newLayer.id,
        expData,
        gridDto,
      );
      break;
    case LayerTypeDTO.Random:
      newLayer.layerDetail = copyRandomLayerDetail(
        sourceLayer,
        expId,
        newLayer.id,
        expData,
      );
      break;
    case LayerTypeDTO.Fragment:
      newLayer.layerDetail = copyFragmentLayerDetail(sourceLayer, expId, newLayer.id);
      break;
    case LayerTypeDTO.Seed:
      newLayer.layerDetail = copySeedLayerDetail(sourceLayer, expId, newLayer.id);
      break;
  }
  return newLayer;
}

function copyCustomLayerDetail(
  sourceLayer: LayerDTO,
  expId: number,
  layerId: number,
  expData: ExperimentDtoClient,
): LayerDetailsDTO {
  const layerDetail = new CustomLayerDTO();
  const srcDetail = sourceLayer.layerDetail as CustomLayerDTO;

  layerDetail.experimentId = expId;
  layerDetail.wellVolume = srcDetail.wellVolume;
  layerDetail.layerId = layerId;
  layerDetail.id = getNewId();
  layerDetail.condition = [];
  for (const srcCond of srcDetail.condition) {
    const srcCondition = srcCond;
    const newCondition = new ConditionDTO();
    newCondition.id = getNewId();
    newCondition.conditionNumber = srcCondition.conditionNumber;
    newCondition.conditionListLayerId = layerDetail.id;
    newCondition.conditionIngredient = [];
    for (const condIng of srcCondition.conditionIngredient) {
      const srcIngredient = condIng;
      const newIngredient = new ConditionIngredientDTO();
      newIngredient.id = getNewId();
      newIngredient.conditionId = newCondition.id;
      newIngredient.concentration = srcIngredient.concentration;
      newIngredient.highPhingredientStockId = srcIngredient.highPhingredientStockId;
      newIngredient.ingredientIngredientTypeId = srcIngredient.ingredientIngredientTypeId;
      newIngredient.ingredientStockId = srcIngredient.ingredientStockId;
      newIngredient.pH = srcIngredient.pH;
      newCondition.conditionIngredient.push(newIngredient);
    }
    layerDetail.condition.push(newCondition);
  }
  return layerDetail;
}

function copyProteinLayerDetail(
  sourceLayer: LayerDTO,
  expId: number,
  layerId: number,
): LayerDetailsDTO {
  const layerDetail = new ProteinLayerDTO();
  const srcDetail = sourceLayer.layerDetail as ProteinLayerDTO;
  layerDetail.experimentId = expId;
  layerDetail.id = getNewId();
  layerDetail.layerId = layerId;
  layerDetail.proteinLayerProteinFormulation = [];
  for (const srcProtein of srcDetail.proteinLayerProteinFormulation) {
    const srcPf = srcProtein;
    const newPf = new ProteinLayerProteinFormulationDTO();
    newPf.proteinDropVolumeVaryAlong = srcPf.proteinDropVolumeVaryAlong;

    if (!isNullOrUndefined(srcPf.proteinFormulation)) {
      newPf.proteinFormulation = new ProteinFormulationDTO();
      newPf.proteinFormulation.id = srcPf.proteinFormulation.id;
      newPf.proteinFormulation.pfName = srcPf.proteinFormulation.pfName;
      newPf.proteinFormulation.proteinLotId = srcPf.proteinFormulation.proteinLotId;
      newPf.proteinFormulation.treeNodeId = srcPf.proteinFormulation.treeNodeId;
      newPf.proteinFormulation.volume = srcPf.proteinFormulation.volume;
      newPf.proteinFormulation.pfIng = srcPf.proteinFormulation.pfIng;
      newPf.proteinFormulation.pfStIng = srcPf.proteinFormulation.pfStIng;
    }

    newPf.proteinFormulationId = srcPf.proteinFormulationId;
    newPf.proteinLayerId = layerId;
    newPf.startProteinDropVolume = srcPf.startProteinDropVolume;
    newPf.startWellDropVolume = srcPf.startWellDropVolume;
    newPf.stopProteinDropVolume = srcPf.stopProteinDropVolume;
    newPf.stopWellDropVolume = srcPf.stopWellDropVolume;
    newPf.wellDropVolumeVaryAlong = srcPf.wellDropVolumeVaryAlong;
    layerDetail.proteinLayerProteinFormulation.push(newPf);
  }

  return layerDetail;
}

function copyFragmentLayerDetail(
  sourceLayer: LayerDTO,
  expId: number,
  layerId: number,
): LayerDetailsDTO {
  const layerDetail = new FragmentLayerDTO();
  const srcDetail = sourceLayer.layerDetail as FragmentLayerDTO;
  layerDetail.id = getNewId();
  layerDetail.layerId = layerId;
  layerDetail.experimentId = expId;
  layerDetail.volume = srcDetail.volume ?? 0;
  layerDetail.fragmentScreenId = srcDetail.fragmentScreenId;
  layerDetail.startingWellNumber = srcDetail.startingWellNumber;
  layerDetail.fragmentScreen = new FragmentScreen();
  layerDetail.fragmentScreen.id = srcDetail.fragmentScreen.id;
  layerDetail.fragmentScreen.treeNodeId = srcDetail.fragmentScreen.treeNodeId;
  layerDetail.fragmentScreen.treeNode = new TreeNode({
    ...srcDetail.fragmentScreen.treeNode,
  });

  layerDetail.fragmentScreen.fragmentFragmentScreen =
    srcDetail.fragmentScreen.fragmentFragmentScreen.map((frag) => {
      const newFragment = new FragmentFragmentScreen();
      newFragment.id = frag.id;
      newFragment.fragmentScreenId = frag.fragmentScreenId;
      newFragment.fragmentId = frag.fragmentId;
      newFragment.concentration = frag.concentration;
      newFragment.conditionNumber = frag.conditionNumber;
      newFragment.notes = frag.notes;
      return newFragment;
    });

  layerDetail.fragmentScreen.fragmentLayer = srcDetail.fragmentScreen.fragmentLayer.map(
    (layer) => {
      const newLayer = new FragmentLayer();
      newLayer.id = getNewId();
      newLayer.layerId = layer.layerId;
      newLayer.experimentId = layer.experimentId;
      newLayer.startingWellNumber = layer.startingWellNumber;
      newLayer.fragmentScreenId = layer.fragmentScreenId;
      return newLayer;
    },
  );
  return layerDetail;
}

function copyScreenLayerDetail(
  sourceLayer: LayerDTO,
  expId: number,
  layerId: number,
): LayerDetailsDTO {
  const layerDetail = new ScreenLayerDTO();
  const srcDetail = sourceLayer.layerDetail as ScreenLayerDTO;
  layerDetail.experimentId = expId;
  layerDetail.id = getNewId();
  layerDetail.layerId = layerId;
  layerDetail.screenLot = new ScreenLotDTO();
  layerDetail.screenLot.init(srcDetail.screenLot);

  return layerDetail;
}

function copyGridLayerDetail(
  sourceLayer: LayerDTO,
  expId: number,
  layerId: number,
  expData: ExperimentDtoClient,
  gridDto?: GridDTO,
): LayerDetailsDTO {
  const layerDetail = new GridLayerDTO();
  const grdDetail = sourceLayer.layerDetail as GridLayerDTO;

  if (!gridDto) {
    gridDto = getLinkedLayerGridDto(expData.layers, grdDetail);
  }

  layerDetail.id = getNewId();
  layerDetail.layerId = layerId;
  layerDetail.subGridNumber = grdDetail.subGridNumber;
  if (grdDetail.subGridNumber > 1) {
    layerDetail.gridId = gridDto?.id;
    layerDetail.grid = undefined;
  } else {
    layerDetail.grid = new GridDTO();
    layerDetail.grid.id = getNewId();
    layerDetail.grid.experimentId = expId;
    layerDetail.grid.startWellVolume = gridDto.startWellVolume;
    layerDetail.grid.stopWellVolume = gridDto.stopWellVolume;
    layerDetail.grid.wellVolumeVaryAlong = gridDto.wellVolumeVaryAlong;
    layerDetail.grid.gridIngredient = [];
    layerDetail.gridId = layerDetail.grid.id;

    for (const srcGridIng of gridDto.gridIngredient) {
      const srcGrdIngredient = srcGridIng;
      const newGrdIngredient = new GridIngredientDTO();

      newGrdIngredient.id = getNewId();
      newGrdIngredient.gridId = layerDetail.grid.id;
      newGrdIngredient.ingredientIngredientTypeId =
        srcGrdIngredient.ingredientIngredientTypeId;
      newGrdIngredient.ingredientStockId = srcGrdIngredient.ingredientStockId;
      newGrdIngredient.highPhingredientStockId = srcGrdIngredient.highPhingredientStockId;
      newGrdIngredient.startConcentration = srcGrdIngredient.startConcentration;
      newGrdIngredient.stopConcentration = srcGrdIngredient.stopConcentration;
      newGrdIngredient.concentrationVaryAlong = srcGrdIngredient.concentrationVaryAlong;
      newGrdIngredient.startpH = srcGrdIngredient.startpH;
      newGrdIngredient.stoppH = srcGrdIngredient.stoppH;
      newGrdIngredient.pHvaryAlong = srcGrdIngredient.pHvaryAlong;
      layerDetail.grid.gridIngredient.push(newGrdIngredient);
    }
  }
  return layerDetail;
}

function copyRandomLayerDetail(
  sourceLayer: LayerDTO,
  expId: number,
  layerId: number,
  expData: ExperimentDtoClient,
): LayerDetailsDTO {
  const layerDetail = new RandomLayerDTO();
  const srcDetail = sourceLayer.layerDetail as RandomLayerDTO;

  layerDetail.experimentId = expId;
  layerDetail.gridSteps = srcDetail.gridSteps;
  layerDetail.id = getNewId();
  layerDetail.layerId = layerId;
  layerDetail.wellVolume = srcDetail.wellVolume;
  layerDetail.randomLayerIngredientGroup = [];
  layerDetail.randomLayerExcludeList = [];

  for (const excList of srcDetail.randomLayerExcludeList) {
    const srcRandomLayerExclude = excList;
    const newRandomLayerExlude = new RandomLayerExcludeListDTO();

    newRandomLayerExlude.id = getNewId();
    newRandomLayerExlude.randomLayerId = layerDetail.id;
    newRandomLayerExlude.ingredient1Id = srcRandomLayerExclude.ingredient1Id;
    newRandomLayerExlude.ingredient2Id = srcRandomLayerExclude.ingredient2Id;
    newRandomLayerExlude.ingredient1 = new IngredientDTO();
    newRandomLayerExlude.ingredient2 = new IngredientDTO();
    newRandomLayerExlude.ingredient1.init(srcRandomLayerExclude.ingredient1);
    newRandomLayerExlude.ingredient2.init(srcRandomLayerExclude.ingredient2);
    layerDetail.randomLayerExcludeList.push(newRandomLayerExlude);
  }

  for (const ingGroup of srcDetail.randomLayerIngredientGroup) {
    const srcIngredientGroup = ingGroup;
    const newIngredientGroup = new RandomLayerIngredientGroupDTO();

    newIngredientGroup.id = getNewId();
    newIngredientGroup.name = srcIngredientGroup.name;
    newIngredientGroup.randomLayerGroupIngredient = [];
    newIngredientGroup.randomLayerGenerationList = [];

    for (const groupIng of srcIngredientGroup.randomLayerGroupIngredient) {
      const srcRandomLayerGroupIngredient = groupIng;
      const newRandomLayerGroupIngredient = new RandomLayerGroupIngredientDTO();

      newRandomLayerGroupIngredient.id = getNewId();
      newRandomLayerGroupIngredient.randomLayerIngredientGroupId = newIngredientGroup.id;
      newRandomLayerGroupIngredient.ingredientStockId =
        srcRandomLayerGroupIngredient.ingredientStockId;
      newRandomLayerGroupIngredient.highPhingredientStockId =
        srcRandomLayerGroupIngredient.highPhingredientStockId;
      newRandomLayerGroupIngredient.ingredientIngredientTypeId =
        srcRandomLayerGroupIngredient.ingredientIngredientTypeId;
      newRandomLayerGroupIngredient.lowConcentration =
        srcRandomLayerGroupIngredient.lowConcentration;
      newRandomLayerGroupIngredient.highConcentration =
        srcRandomLayerGroupIngredient.highConcentration;
      newRandomLayerGroupIngredient.lowPh = srcRandomLayerGroupIngredient.lowPh;
      newRandomLayerGroupIngredient.highPh = srcRandomLayerGroupIngredient.highPh;
      newRandomLayerGroupIngredient.weight = srcRandomLayerGroupIngredient.weight;

      newIngredientGroup.randomLayerGroupIngredient.push(newRandomLayerGroupIngredient);
    }

    for (const genList of srcIngredientGroup.randomLayerGenerationList) {
      const srcRandomLayerGenerationList = genList;
      const newRandomLayerGenerationList = new RandomLayerGenerationListDTO();

      newRandomLayerGenerationList.id = getNewId();
      newRandomLayerGenerationList.randomLayerId = layerDetail.id;
      newRandomLayerGenerationList.randomLayerIngredientGroupId = newIngredientGroup.id;
      newRandomLayerGenerationList.probability = srcRandomLayerGenerationList.probability;
      newIngredientGroup.randomLayerGenerationList.push(newRandomLayerGenerationList);
    }

    layerDetail.randomLayerIngredientGroup.push(newIngredientGroup);
  }
  return layerDetail;
}

function copySeedLayerDetail(
  sourceLayer: LayerDTO,
  expId: number,
  layerId: number,
): LayerDetailsDTO {
  const layerDetail = new SeedLayerDetailsDTO();
  const srcDetail = sourceLayer.layerDetail as SeedLayerDetailsDTO;

  layerDetail.experimentId = srcDetail.experimentId;
  layerDetail.id = getNewId();
  layerDetail.layerId = layerId;
  layerDetail.seedDropVolumeVaryAlong = srcDetail.seedDropVolumeVaryAlong;
  layerDetail.seedId = srcDetail.seedId;
  layerDetail.startSeedDropVolume = srcDetail.startSeedDropVolume;
  layerDetail.stopSeedDropVolume = srcDetail.stopSeedDropVolume;
  layerDetail.seedSeedLayerId = getNewId();

  return layerDetail;
}

export function initCustomLayer(
  docData: ExperimentDtoClient,
  numOfConditions: number,
): LayerDTO {
  const newLayer = new LayerDTO();
  newLayer.name = getUniqueLayerName(docData.layers, 'Custom Layer');
  newLayer.id = getNewId();
  newLayer.y = 0;
  newLayer.x = 0;
  //// need to validate number of conditions, if greater than size of canvas then try resize or show error
  newLayer.width =
    Math.min(numOfConditions, docData.containerCols * docData.colCount) - 1;
  newLayer.height =
    Math.ceil(numOfConditions / (docData.containerCols * docData.colCount)) - 1;
  newLayer.volume = getDefaultVolume(docData);
  newLayer.targetDrop = -1;
  newLayer.overrideLayer = false;
  newLayer.locked = false;
  newLayer.expId = docData.experimentId;
  newLayer.layerState = LayerStateDTO.New;
  newLayer.volumeBasedAddition = false;
  newLayer.dirty = true;
  newLayer.expId = docData.experimentId;
  newLayer.type = LayerTypeDTO.ConditionList;
  newLayer.layerDetail = initLayerDetail(
    LayerTypeDTO.ConditionList,
    newLayer.id,
    docData,
  );
  return newLayer;
}

export const pasteNewLayerUtil = (
  expData,
  layerType: LayerTypeDTO,
  selectedWells: CopyWellDetail[],
  wellNumber: number,
): any => {
  switch (layerType) {
    case LayerTypeDTO.Grid:
      selectedWells.forEach((well, i) => {
        const { newLayer, layerDetail } = initLayer(
          i,
          layerType,
          expData as ExperimentDtoClient,
          selectedWells.length,
        );
        well.wellLayers.forEach((wl: WellLayer) => {
          wl.wellLayerIngredientList.forEach((wli: WellLayerIngredientDto) => {
            const gridIng: GridIngredientDTO = new GridIngredientDTO();
            gridIng.gridId = layerDetail.gridId;
            gridIng.id = getNewId();
            gridIng.highPhingredientStockId = wli.highPhIngStockId;
            gridIng.ingredientIngredientTypeId = wli.ingredientIngredientTypeId;
            gridIng.ingredientStockId = wli.ingredientStockId;
            gridIng.pHvaryAlong = EGridVaryAlong.NONE;
            gridIng.startConcentration = wli.concentration;
            gridIng.startpH = wli.pH;
            gridIng.concentrationVaryAlong = EGridVaryAlong.NONE;
            layerDetail.grid.gridIngredient.push(gridIng);
          });
        });
        newLayer.layerDetail = layerDetail;
        expData.layers = [newLayer, ...expData.layers];
      });
      break;
    case LayerTypeDTO.ConditionList:
      const result = initLayer(
        -1,
        layerType,
        expData as ExperimentDtoClient,
        selectedWells,
        wellNumber,
      );
      selectedWells.forEach((well, i) => {
        const condition = new ConditionDTO();
        condition.id = getNewId();
        condition.conditionListLayerId = result.layerDetail.id;
        condition.conditionNumber = i + 1;
        condition.conditionIngredient = [];

        well.wellLayers.forEach((wl: WellLayer) => {
          wl.wellLayerIngredientList.forEach((wli: WellLayerIngredientDto) => {
            const conditionIng = new ConditionIngredientDTO();
            conditionIng.id = getNewId();
            conditionIng.conditionId = condition.id;
            conditionIng.concentration = wli.concentration;
            conditionIng.highPhingredientStockId = wli.highPhIngStockId;
            conditionIng.ingredientIngredientTypeId = wli.ingredientIngredientTypeId;
            conditionIng.ingredientStockId = wli.ingredientStockId;
            conditionIng.pH = wli.pH;
            condition.conditionIngredient.push(conditionIng);
          });
        });
        (result.layerDetail as CustomLayerDTO).condition.push(condition);
      });
      result.newLayer.layerDetail = result.layerDetail;
      expData.layers = [...expData.layers, result.newLayer];
      break;

    case LayerTypeDTO.Random:
      const { newLayer, layerDetail } = initLayer(
        -1,
        layerType,
        expData as ExperimentDtoClient,
        selectedWells.length,
      );
      const ingTypes = GetIngredients().ingredientType;
      const ingGroups: RandomLayerIngredientGroupDTO[] = [];
      ingTypes.map((it) => {
        const ingGroup: RandomLayerIngredientGroupDTO =
          new RandomLayerIngredientGroupDTO();
        ingGroup.id = getNewId();
        ingGroup.name = it.name;
        ingGroup.randomLayerGenerationList = [];
        ingGroup.randomLayerGroupIngredient = [];
        ingGroups.push(ingGroup);
      });
      addRandomLayerGroupIngredients(ingGroups, newLayer.id, selectedWells);
      (layerDetail as RandomLayerDTO).randomLayerIngredientGroup = ingGroups;
      newLayer.layerDetail = layerDetail;
      expData.layers = [newLayer, ...expData.layers];
      break;
  }
  return expData;
};
